import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Bell, EnvelopeSimple, WhatsappLogo, CheckCircle } from "@phosphor-icons/react";

export default function Notifications() {
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [email, setEmail] = useState({ to: "", subject: "Hello from dashboard", body: "This is a test message." });
  const [wa, setWa] = useState({ to: "+15551234567", body: "Test WhatsApp alert" });

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifs(data.notifications);
      setUnread(data.unread);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  useEffect(() => { load(); }, []);

  const markAllRead = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  const sendEmail = async () => {
    try {
      const { data } = await api.post("/notify/email", email);
      toast.success(`Email queued (${data.provider}${data.provider === "mock" ? " — MOCKED" : ""})`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendWa = async () => {
    try {
      const { data } = await api.post("/notify/whatsapp", wa);
      toast.success(`WhatsApp queued (${data.provider}${data.provider === "mock" ? " — MOCKED" : ""})`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-8">
      <header className="mb-6 pb-6 border-b border-[#262626] flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#71717A] mb-2">Messaging</div>
          <h1 className="font-display text-4xl font-black tracking-tight">Notifications</h1>
        </div>
        {unread > 0 && (
          <Button onClick={markAllRead} variant="outline" className="rounded-sm border-[#262626] bg-transparent hover:bg-[#141414]" data-testid="mark-all-read-btn">
            <CheckCircle size={14} weight="bold" className="mr-2" /> Mark all read ({unread})
          </Button>
        )}
      </header>

      <Tabs defaultValue="inbox" className="w-full">
        <TabsList className="rounded-sm bg-[#141414] border border-[#262626]">
          <TabsTrigger value="inbox" className="rounded-sm data-[state=active]:bg-[#0055FF] data-[state=active]:text-white" data-testid="tab-inbox">Inbox</TabsTrigger>
          <TabsTrigger value="email" className="rounded-sm data-[state=active]:bg-[#0055FF] data-[state=active]:text-white" data-testid="tab-email">Send email</TabsTrigger>
          <TabsTrigger value="whatsapp" className="rounded-sm data-[state=active]:bg-[#0055FF] data-[state=active]:text-white" data-testid="tab-whatsapp">Send WhatsApp</TabsTrigger>
        </TabsList>

        <TabsContent value="inbox" className="mt-4 space-y-2" data-testid="notifications-list">
          {notifs.length === 0 && (
            <div className="dashed border border-[#262626] p-12 text-center text-[#71717A]">
              <Bell size={22} weight="bold" className="mx-auto mb-2" />
              No notifications yet.
            </div>
          )}
          {notifs.map((n) => (
            <div key={n.id} className={`border p-4 flex gap-4 ${n.read ? "border-[#262626] bg-[#0F0F0F]" : "border-[#0055FF]/40 bg-[#141414]"}`}>
              <Bell size={16} weight="bold" className={n.read ? "text-[#71717A]" : "text-[#0055FF]"} />
              <div className="flex-1">
                <div className="text-sm font-semibold">{n.title}</div>
                <div className="text-sm text-[#A1A1AA]">{n.message}</div>
                <div className="text-xs text-[#71717A] font-mono mt-1">{new Date(n.created_at).toLocaleString()}</div>
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="email" className="mt-4">
          <Card className="rounded-sm bg-[#141414] border-[#262626] p-6 max-w-2xl">
            <div className="flex items-center gap-2 mb-4">
              <EnvelopeSimple size={18} weight="bold" className="text-[#0055FF]" />
              <span className="font-display font-bold">Send transactional email</span>
              <span className="text-[10px] font-mono uppercase tracking-widest text-[#F59E0B]">MOCKED</span>
            </div>
            <div className="space-y-3">
              <div><Label className="text-xs uppercase tracking-wider">To</Label><Input value={email.to} onChange={(e) => setEmail({ ...email, to: e.target.value })} className="mt-1 rounded-sm bg-[#0A0A0A] border-[#262626]" data-testid="email-to-input" /></div>
              <div><Label className="text-xs uppercase tracking-wider">Subject</Label><Input value={email.subject} onChange={(e) => setEmail({ ...email, subject: e.target.value })} className="mt-1 rounded-sm bg-[#0A0A0A] border-[#262626]" data-testid="email-subject-input" /></div>
              <div><Label className="text-xs uppercase tracking-wider">Body</Label><Textarea rows={4} value={email.body} onChange={(e) => setEmail({ ...email, body: e.target.value })} className="mt-1 rounded-sm bg-[#0A0A0A] border-[#262626]" data-testid="email-body-input" /></div>
              <Button onClick={sendEmail} className="rounded-sm bg-[#0055FF] hover:bg-[#0044CC]" data-testid="send-email-btn">Send</Button>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="whatsapp" className="mt-4">
          <Card className="rounded-sm bg-[#141414] border-[#262626] p-6 max-w-2xl">
            <div className="flex items-center gap-2 mb-4">
              <WhatsappLogo size={18} weight="bold" className="text-[#25D366]" />
              <span className="font-display font-bold">Send WhatsApp message</span>
              <span className="text-[10px] font-mono uppercase tracking-widest text-[#F59E0B]">MOCKED</span>
            </div>
            <div className="space-y-3">
              <div><Label className="text-xs uppercase tracking-wider">To (E.164)</Label><Input value={wa.to} onChange={(e) => setWa({ ...wa, to: e.target.value })} className="mt-1 rounded-sm bg-[#0A0A0A] border-[#262626]" data-testid="wa-to-input" /></div>
              <div><Label className="text-xs uppercase tracking-wider">Body</Label><Textarea rows={4} value={wa.body} onChange={(e) => setWa({ ...wa, body: e.target.value })} className="mt-1 rounded-sm bg-[#0A0A0A] border-[#262626]" data-testid="wa-body-input" /></div>
              <Button onClick={sendWa} className="rounded-sm bg-[#0055FF] hover:bg-[#0044CC]" data-testid="send-wa-btn">Send</Button>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
